import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { authService } from '../services/auth.service';
import { PermissionSummary, Environment, Operation } from '../services/permissions.service';

interface User {
  id: string;
  email: string;
  name: string;
  roles: string[];
  groups: string[];
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  hasAccess: boolean;
  accessDenied: boolean;
  permissions: PermissionSummary | null;
  userRole: string;
  userRoleDescription: string;
  canAccessDomain: (domainId: string) => boolean;
  getUserDomains: () => string[];
  getDomainPermissions: (domainId: string) => any;
  canPerformOperation: (domainId: string, environment: Environment, operation: Operation) => boolean;
  canAccessEnvironment: (domainId: string, environment: Environment) => boolean;
  canModifyEnvironment: (domainId: string, environment: Environment) => boolean;
  canSyncBetweenEnvironments: (domainId: string, sourceEnv: Environment, targetEnv: Environment) => boolean;
  login: () => void;
  logout: () => void;
  checkAuth: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [accessDenied, setAccessDenied] = useState(false);
  const [permissions, setPermissions] = useState<PermissionSummary | null>(null);

  const checkAuth = async () => {
    try {
      setIsLoading(true);
      setAccessDenied(false);
      const status = await authService.checkAuthStatus();

      if (status.authenticated && status.user) {
        setUser(status.user);
        setPermissions(status.permissions || null);

        // Check if user has access to WAD Admin Tools
        if (!authService.hasWADAdminAccess(status.user)) {
          setAccessDenied(true);
        }
      } else {
        setUser(null);
        setPermissions(null);
        setAccessDenied(false);
      }
    } catch (error) {
      console.error('Failed to check auth status:', error);
      setUser(null);
      setPermissions(null);
      setAccessDenied(false);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    checkAuth();
  }, []);

  const login = () => {
    // In production, use same origin (empty string means relative URLs)
    // In development, explicitly point to the backend server
    const API_URL = window.location.origin.includes('localhost')
      ? 'http://localhost:5003'
      : ''; // Empty string for same-origin requests in production
    const returnUrl = encodeURIComponent(window.location.origin);
    window.location.href = `${API_URL}/auth/login?returnUrl=${returnUrl}`;
  };

  const logout = async () => {
    try {
      await authService.logout();
      setUser(null);
      setPermissions(null);
      setAccessDenied(false);
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  // Permission helper functions
  const canAccessDomain = (domainId: string): boolean => {
    return authService.canAccessDomain(user, domainId);
  };

  const getUserDomains = (): string[] => {
    return authService.getUserDomains(user);
  };

  const getDomainPermissions = (domainId: string) => {
    return authService.getDomainPermissions(user, domainId);
  };

  const canPerformOperation = (domainId: string, environment: Environment, operation: Operation): boolean => {
    return authService.canPerformOperation(user, domainId, environment, operation);
  };

  const canAccessEnvironment = (domainId: string, environment: Environment): boolean => {
    return authService.canAccessEnvironment(user, domainId, environment);
  };

  const canModifyEnvironment = (domainId: string, environment: Environment): boolean => {
    return authService.canModifyEnvironment(user, domainId, environment);
  };

  const canSyncBetweenEnvironments = (domainId: string, sourceEnv: Environment, targetEnv: Environment): boolean => {
    return authService.canSyncBetweenEnvironments(user, domainId, sourceEnv, targetEnv);
  };

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    isLoading,
    hasAccess: !!user && authService.hasWADAdminAccess(user),
    accessDenied,
    permissions,
    userRole: authService.getUserRole(user),
    userRoleDescription: authService.getRoleDescription(user),
    canAccessDomain,
    getUserDomains,
    getDomainPermissions,
    canPerformOperation,
    canAccessEnvironment,
    canModifyEnvironment,
    canSyncBetweenEnvironments,
    login,
    logout,
    checkAuth
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};